import sitecoreIds from 'src/app_config/sitecore-ids.json';

import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

import { FilterGroup } from 'src/types/FilterGroup';
import { ResourceListItem } from 'src/types/ResourceListItem';

import BookSvg from 'public/icons/Book.svg';
import ClockSvg from 'public/icons/clock.svg';
import MarkerSvg from 'public/icons/Marker.svg';
import PeopleSvg from 'public/icons/People.svg';
import PersonTickSvg from 'public/icons/PersonTick.svg';
import PriceTagSvg from 'public/icons/PriceTag.svg';

import styles from './_CardAttributeList.module.css';

export type CardAttributeListProps = {
	item: ResourceListItem;
	filters?: FilterGroup[] | undefined;
	registeredUserText?: Field<string>;
	rcnMemberText?: Field<string>;
	rcniPlusSubscriberText?: Field<string>;
	organisationalProductText?: Field<string>;
	openAccessText?: Field<string>;
	cpdHoursText?: Field<string>;
};

const {
	OpenAccessContentAccessTypeId,
	RegisteredUserContentAccessTypeId,
	RcnMemberContentAccessTypeId,
	RcniPlusSubscriberContentAccessTypeId,
	OrganisationalProductContentAccessTypeId,
	TypeOfAccessFilterId,
} = sitecoreIds.Items;

export default function CardAttributeList(props: CardAttributeListProps): JSX.Element {
	const getAccessTypeString = () => {
		if (!props.filters) {
			return props.item.AccessTypesString;
		}

		const accessIds = Array.from((props.item.AccessTypes as Map<string, string>).values()).map((AccessType) => {
			const access = props.filters
				?.find((filter) => filter.id === TypeOfAccessFilterId)
				?.options?.find((accessOption) => accessOption.title.toLowerCase() === AccessType.toLowerCase());
			return access?.id;
		});

		return getTextValue(accessIds);
	};

	const getTextValue = (accessIds: (string | undefined)[]) => {
		if (accessIds.includes(OpenAccessContentAccessTypeId)) {
			return props.openAccessText?.value;
		}

		if (accessIds.includes(RegisteredUserContentAccessTypeId)) {
			return props.registeredUserText?.value;
		}

		if (accessIds.includes(RcnMemberContentAccessTypeId) && accessIds.includes(RcniPlusSubscriberContentAccessTypeId)) {
			return (props.rcnMemberText?.value + ' - ' + props.rcniPlusSubscriberText?.value).replaceAll('only', '').trim();
		}

		if (accessIds.includes(RcnMemberContentAccessTypeId)) {
			return props.rcnMemberText?.value;
		}

		if (accessIds.includes(RcniPlusSubscriberContentAccessTypeId)) {
			return props.rcniPlusSubscriberText?.value;
		}

		if (accessIds.includes(OrganisationalProductContentAccessTypeId)) {
			return props.organisationalProductText?.value;
		}
		return '';
	};

	const listComponent = (icon: JSX.Element, item?: string) => {
		return (
			<li className={styles.metaItem}>
				<span>{icon}</span>
				<span className={styles.metaText}>{item}</span>
			</li>
		);
	};

	const typesOfResources =
		props.item.TypesOfResources && props.item.TypesOfResources.length > 0 ? props.item.TypesOfResources.join(', ') : undefined;

	const learningType = props.item.LearningType;
	const accessTypeString = getAccessTypeString();

	let cpdHoursString = props.item.CpdHours;
	if (props.item.CpdHours) {
		const timeRegex = /([0-9]?[0-9]):([0-9][0-9]):([0-9][0-9])/;
		const matches = props.item.CpdHours.match(timeRegex);
		if (matches && matches.length > 3) {
			const hours = parseInt(matches[1]);
			const minutes = parseInt(matches[2]);

			const totalHours = hours + minutes / 60;

			cpdHoursString = (minutes > 0 ? totalHours.toFixed(1) : totalHours.toString()) + ' ' + props.cpdHoursText?.value;
		}
	}

	return (
		<ul className={styles.metaList}>
			{(typesOfResources || learningType) && listComponent(<BookSvg className={styles.metaIcon} />, typesOfResources || learningType)}

			{props.item.AccessTypes && accessTypeString && listComponent(<PersonTickSvg className={styles.metaIcon} />, accessTypeString)}

			{props.item.LearningFormats && listComponent(<PeopleSvg className={styles.metaIcon} />, props.item.LearningFormats.join(', '))}

			{props.item.Location && listComponent(<MarkerSvg className={styles.metaIcon} />, props.item.Location)}

			{props.item.Cost && listComponent(<PriceTagSvg className={styles.metaIcon} />, props.item.Cost)}

			{props.item.CpdHours && listComponent(<ClockSvg className={styles.metaIcon} />, cpdHoursString)}
		</ul>
	);
}
